import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

import Avatar from 'components/Avatar';
import { getMentionToken } from 'utils/commentMentions';

const findMentionTrigger = (value, cursor) => {
  const textBeforeCursor = value.slice(0, cursor);
  const match = textBeforeCursor.match(/(^|\s)@([^@\n]*)$/);
  if (!match) return null;

  return {
    start: textBeforeCursor.lastIndexOf('@'),
    end: cursor,
    query: match[2].trim().toLowerCase(),
  };
};

const CommentMentionTextarea = ({
  ariaLabel,
  className,
  disabled,
  inputRef,
  maxLength,
  mentionableUsers,
  mentionedUserIds,
  onChange,
  onKeyDown,
  onMentionedUserIdsChange,
  placeholder,
  value,
}) => {
  const textareaRef = useRef(null);
  const [trigger, setTrigger] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [suggestionPosition, setSuggestionPosition] = useState(null);
  const suggestions = useMemo(() => {
    if (!trigger) return [];
    const query = trigger.query;
    return mentionableUsers
      .filter(user => {
        const searchable = `${user.name || ''} ${
          user.email || ''
        }`.toLowerCase();
        return !query || searchable.includes(query);
      })
      .slice(0, 6);
  }, [mentionableUsers, trigger]);

  const updateTrigger = (nextValue, cursor) => {
    const nextTrigger = findMentionTrigger(nextValue, cursor);
    setTrigger(nextTrigger);
    setHighlightedIndex(0);
  };

  const setTextareaRef = useCallback(
    node => {
      textareaRef.current = node;
      if (inputRef) inputRef.current = node;
    },
    [inputRef]
  );

  const updateSuggestionPosition = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea || typeof window === 'undefined') return;
    const rect = textarea.getBoundingClientRect();
    const margin = 12;
    const gap = 6;
    const estimatedHeight = 208;
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const width = Math.min(
      Math.max(rect.width, 288),
      viewportWidth - margin * 2
    );
    const left = Math.min(
      Math.max(rect.left, margin),
      viewportWidth - width - margin
    );
    const spaceBelow = Math.max(0, viewportHeight - rect.bottom - gap - margin);
    const spaceAbove = Math.max(0, rect.top - gap - margin);
    const opensBelow =
      spaceBelow >= estimatedHeight || spaceBelow >= spaceAbove;
    const availableHeight = opensBelow ? spaceBelow : spaceAbove;
    setSuggestionPosition({
      left,
      maxHeight: Math.min(estimatedHeight, Math.max(72, availableHeight)),
      top: opensBelow ? rect.bottom + gap : rect.top - gap,
      transform: opensBelow ? undefined : 'translateY(-100%)',
      width,
    });
  }, []);

  const showSuggestions = Boolean(trigger && suggestions.length);
  useLayoutEffect(() => {
    if (showSuggestions) updateSuggestionPosition();
  }, [showSuggestions, suggestions.length, updateSuggestionPosition]);

  useEffect(() => {
    if (!showSuggestions) return undefined;
    window.addEventListener('resize', updateSuggestionPosition);
    window.addEventListener('scroll', updateSuggestionPosition, true);
    return () => {
      window.removeEventListener('resize', updateSuggestionPosition);
      window.removeEventListener('scroll', updateSuggestionPosition, true);
    };
  }, [showSuggestions, updateSuggestionPosition]);

  const handleChange = event => {
    const nextValue = event.target.value;
    const retainedMentionIds = mentionedUserIds.filter(userId => {
      const user = mentionableUsers.find(
        candidate => candidate.id?.toString() === userId?.toString()
      );
      return user && nextValue.includes(getMentionToken(user));
    });
    if (retainedMentionIds.length !== mentionedUserIds.length) {
      onMentionedUserIdsChange(retainedMentionIds);
    }
    onChange(nextValue);
    updateTrigger(nextValue, event.target.selectionStart);
  };

  const selectSuggestion = user => {
    if (!trigger) return;
    const token = `${getMentionToken(user)} `;
    const nextValue = `${value.slice(0, trigger.start)}${token}${value.slice(
      trigger.end
    )}`.slice(0, maxLength);
    const nextMentionIds = mentionedUserIds.some(
      userId => userId?.toString() === user.id?.toString()
    )
      ? mentionedUserIds
      : [...mentionedUserIds, user.id];
    const nextCursor = Math.min(trigger.start + token.length, nextValue.length);

    onChange(nextValue);
    onMentionedUserIdsChange(nextMentionIds);
    setTrigger(null);
    window.requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const handleKeyDown = event => {
    if (trigger && suggestions.length && !event.shiftKey) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedIndex(index => (index + 1) % suggestions.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedIndex(
          index => (index - 1 + suggestions.length) % suggestions.length
        );
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        selectSuggestion(suggestions[highlightedIndex]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setTrigger(null);
        return;
      }
    }
    onKeyDown?.(event);
  };

  const suggestionMenu =
    showSuggestions && suggestionPosition && typeof document !== 'undefined'
      ? createPortal(
          <div
            aria-label="Mention a collaborator"
            className="comment-mention-input__suggestions"
            id="comment-mentions"
            role="listbox"
            style={suggestionPosition}
          >
            {suggestions.map((user, index) => (
              <button
                aria-selected={index === highlightedIndex}
                className={`comment-mention-input__suggestion ${
                  index === highlightedIndex ? 'is-highlighted' : ''
                }`}
                key={user.id}
                onMouseDown={event => {
                  event.preventDefault();
                  selectSuggestion(user);
                }}
                role="option"
                type="button"
              >
                <Avatar
                  className="comment-mention-input__avatar"
                  image={
                    user.profileImage ? (
                      <img src={user.profileImage} alt="" />
                    ) : null
                  }
                  name={user.name || user.email}
                />
                <span className="comment-mention-input__suggestion-copy">
                  <strong className="comment-mention-input__suggestion-name">
                    {user.name || user.email}
                  </strong>
                  {user.name && user.email ? (
                    <small className="comment-mention-input__suggestion-email">
                      {user.email}
                    </small>
                  ) : null}
                </span>
              </button>
            ))}
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <div className={`comment-mention-input ${className || ''}`}>
        <textarea
          aria-label={ariaLabel}
          aria-autocomplete="list"
          aria-controls={
            trigger && suggestions.length ? 'comment-mentions' : undefined
          }
          disabled={disabled}
          maxLength={maxLength}
          onBlur={() => window.setTimeout(() => setTrigger(null), 100)}
          onChange={handleChange}
          onClick={event =>
            updateTrigger(value, event.currentTarget.selectionStart)
          }
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          ref={setTextareaRef}
          value={value}
        />
      </div>
      {suggestionMenu}
    </>
  );
};

CommentMentionTextarea.propTypes = {
  ariaLabel: PropTypes.string.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  inputRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  maxLength: PropTypes.number,
  mentionableUsers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string,
      email: PropTypes.string,
      profileImage: PropTypes.string,
    })
  ),
  mentionedUserIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  onMentionedUserIdsChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.string.isRequired,
};

CommentMentionTextarea.defaultProps = {
  className: '',
  disabled: false,
  inputRef: null,
  maxLength: 2000,
  mentionableUsers: [],
  mentionedUserIds: [],
  onKeyDown: null,
  placeholder: '',
};

export default CommentMentionTextarea;
